import React, { useState, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import './TeacherTestView.css'
import LoadingScreen from '../components/LoadingScreen'
import alert from '../utils/alertService'
import ConfirmModal from '../components/ConfirmModal'

const TeacherTestView = () => {
  const { testId } = useParams()
  const history = useHistory()
  const [test, setTest] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

  useEffect(() => {
    const fetchTestDetails = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(`/api/teacher/tests/${testId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setTest(data.test)
      } else {
        setError('Failed to load test details')
      }
    } catch (err) {
      setError('Network error')
    } finally {
      setLoading(false)
    }
    }

    fetchTestDetails()
  }, [testId])

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'Not set'
    const date = new Date(dateStr)
    return date.toLocaleString()
  }

  const handleEdit = () => {
    history.push(`/teacher/test/${testId}/edit`)
  }

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(`/api/teacher/tests/${testId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        alert.success('Test deleted successfully')
        history.push('/teacher/tests')
      } else {
        alert.error('Failed to delete test')
      }
    } catch (err) {
      alert.error('Network error')
    }
  }

  if (loading) {
    return <LoadingScreen message="Loading Test Details" />
  }

  if (error || !test) {
    return (
      <div className="test-view-container">
        <div className="error-message">{error || 'Test not found'}</div>
        <button onClick={() => history.push('/teacher/tests')} className="back-button">
          Back to Tests
        </button>
      </div>
    )
  }

  return (
    <div className="test-view-container">
      <header className="page-header">
        <div>
          <h1>Test Details</h1>
          <p className="subtitle">View and manage test information</p>
        </div>
        <div className="header-actions">
          <button onClick={handleEdit} className="edit-button">
            Edit Test
          </button>
          <button onClick={() => history.push('/teacher/tests')} className="back-button">
            Back to Tests
          </button>
        </div>
      </header>

      <div className="test-view-content">
        <div className="test-header-card">
          <div className="test-title-section">
            <h2>{test.title}</h2>
            <span className={`status-badge status-${test.status}`}>
              {test.status}
            </span>
          </div>
          <p className="test-description">{test.description}</p>
        </div>

        <div className="test-details-grid">
          <div className="detail-card">
            <div className="detail-icon">⏱️</div>
            <div className="detail-content">
              <h3>Duration</h3>
              <p>{test.duration_minutes || 0} minutes</p>
            </div>
          </div>

          <div className="detail-card">
            <div className="detail-icon">📊</div>
            <div className="detail-content">
              <h3>Total Points</h3>
              <p>{test.total_points || 0} points</p>
            </div>
          </div>

          <div className="detail-card">
            <div className="detail-icon">✅</div>
            <div className="detail-content">
              <h3>Passing Score</h3>
              <p>{test.passing_score || 0}%</p>
            </div>
          </div>

          <div className="detail-card">
            <div className="detail-icon">👥</div>
            <div className="detail-content">
              <h3>Total Attempts</h3>
              <p>{test.total_attempts || 0}</p>
            </div>
          </div>
        </div>

        <div className="test-info-sections">
          <section className="info-section">
            <h3>Schedule</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Start Time:</span>
                <span className="info-value">{formatDateTime(test.start_time)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">End Time:</span>
                <span className="info-value">{formatDateTime(test.end_time)}</span>
              </div>
            </div>
          </section>

          <section className="info-section">
            <h3>Instructions</h3>
            <div className="instructions-box">
              {test.instructions || 'No instructions provided'}
            </div>
          </section>

          <section className="info-section">
            <h3>Proctoring Settings</h3>
            <div className="proctoring-settings">
              <div className="setting-item">
                <span className="setting-icon">
                  {test.proctoring_enabled ? '✅' : '❌'}
                </span>
                <span>Proctoring Enabled</span>
              </div>
              <div className="setting-item">
                <span className="setting-icon">
                  {test.face_detection ? '✅' : '❌'}
                </span>
                <span>Face Detection</span>
              </div>
              <div className="setting-item">
                <span className="setting-icon">
                  {test.tab_switch_detection ? '✅' : '❌'}
                </span>
                <span>Tab Switch Detection</span>
              </div>
              <div className="setting-item">
                <span className="setting-label">Max Violations:</span>
                <span className="setting-value">{test.max_violations || 3}</span>
              </div>
              <div className="setting-item">
                <span className="setting-label">Max Attempts:</span>
                <span className="setting-value">{test.max_attempts || 1}</span>
              </div>
            </div>
          </section>

          <section className="info-section">
            <h3>Metadata</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Created:</span>
                <span className="info-value">{formatDateTime(test.created_at)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Last Updated:</span>
                <span className="info-value">{formatDateTime(test.updated_at)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Test ID:</span>
                <span className="info-value">#{test.id}</span>
              </div>
            </div>
          </section>
        </div>

        <div className="action-buttons">
          <button onClick={handleEdit} className="primary-button">
            Edit Test
          </button>
          <button onClick={() => setDeleteModalOpen(true)} className="delete-button">
            Delete Test
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Test"
        message="Are you sure you want to delete this test? This action cannot be undone."
        confirmText="Delete"
        isDangerous={true}
      />
    </div>
  )
}

export default TeacherTestView

